import React from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import { Navigation } from "swiper/modules";

// Array of small duas
const duas = [
  {
    arabic: "رَبِّ زِدْنِي عِلْمًا",
    pronunciation: "Rabbi zidni ilma",
    translation: "My Lord, increase me in knowledge"
  },
  {
    arabic: "اللهم انفعني بما علمتني",
    pronunciation: "Allahumma anfa‘ni bima ‘allamtani",
    translation: "O Allah, benefit me with what You have taught me"
  },
  {
    arabic: "اللهم اشرح لي صدري",
    pronunciation: "Allahumma ishrah li sadri",
    translation: "O Allah, expand my chest (grant me confidence)"
  },
];

// Comprehensive lessons array
const lessons = [
  {
    title: "Alphabet",
    link: "/alphabet-lesson",
    color: "bg-yellow-200",
    emoji: "🔤"
  },
    {
    title: "Long Vowels",
    link: "/long-vowels-lesson",
    color: "bg-pink-200",
    emoji: "🅰️"
  },
  {
    title: "Words",
    link: "/word-lesson",
    color: "bg-blue-200",
    emoji: "📝"
  },
  {
    title: "Chakl (Harakat)",
    link: "/chakl-lesson",
    color: "bg-purple-200",
    emoji: "ًٌٍَُِّْ"
  },
  {
    title: "Joint Letters",
    link: "/joint-letters",
    color: "bg-orange-200",
    emoji: "🔗"
  },
  {
    title: "Numbers",
    link: "/numbers-lesson",
    color: "bg-purple-200",
    emoji: "🔢"
  },
  {
    title: "Days of Week",
    link: "/days-lesson",
    color: "bg-blue-200",
    emoji: "📅"
  },
  {
    title: "Family Members",
    link: "/family-lesson",
    color: "bg-green-200",
    emoji: "👨‍👩‍👧‍👦"
  },
  {
    title: "Animals",
    link: "/animals-lesson",
    color: "bg-green-200",
    emoji: "🐪"
  },
  {
    title: "Colors",
    link: "/colors-lesson",
    color: "bg-red-200",
    emoji: "🎨"
  },
  {
    title: "Conversation",
    link: "/conversational-lesson",
    color: "bg-teal-200",
    emoji: "💬"
  },
  {
    title: "Surahs",
    link: "/surah-list",
    color: "bg-indigo-200",
    emoji: "📖"
  },
  {
    title: "Prayer Lessons",
    link: "/prayer-lessons",
    color: "bg-red-200",
    emoji: "🕌"
  },
  {
    title: "Prophet Stories",
    link: "/prophet-stories",
    color: "bg-yellow-100",
    emoji: "🌟"
  },
  {
    title: "Wudu (Ablution)",
    link: "/wudu-lesson",
    color: "bg-blue-100",
    emoji: "💧"
  },
];

export default function LessonsPage() {
  // Use a media query to detect mobile
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (isMobile) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
        {/* Header Section */}
        <div className="bg-white shadow-sm border-b border-gray-100 px-4 py-6">
          <div className="flex items-center justify-center">
            <div className="text-3xl mb-2">📚</div>
          </div>
          <h1 className="text-2xl font-bold text-gray-800 text-center mb-1">
            Arabic Lessons
          </h1>
          <p className="text-sm text-gray-600 text-center">
            Choose a lesson to start learning
          </p>
        </div>

        {/* Sticky Daily Duas Section */}
        <div className="sticky top-0 z-20 bg-white/95 backdrop-blur-sm border-b border-gray-100 px-4 py-4">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-2 mb-3">
              <div className="text-lg">🤲</div>
              <h2 className="font-semibold text-gray-800">Daily Duas</h2>
            </div>
            <Swiper
              loop={true}
              spaceBetween={10}
              slidesPerView={1}
              navigation={true}
              modules={[Navigation]}
              className="w-full"
            >
              {duas.map((dua, idx) => (
                <SwiperSlide key={idx}>
                  <div className="bg-blue-50 rounded-lg p-3 text-center">
                    <div className="text-sm font-bold text-gray-800 mb-1" dir="rtl">{dua.arabic}</div>
                    <div className="text-xs text-blue-600 font-medium mb-1">{dua.pronunciation}</div>
                    <div className="text-xs text-gray-600 italic">{dua.translation}</div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>

        {/* Scrollable Lessons Grid */}
        <div className="px-4 pb-6 pt-4">
          <h2 className="text-lg font-semibold text-gray-800 px-2 mb-4">All Lessons</h2>
          <div className="grid grid-cols-1 gap-4">
            {lessons.map((lesson, idx) => (
              <Link
                key={idx}
                to={lesson.link}
                className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-200 active:scale-98 flex items-center space-x-4"
              >
                <div className="bg-blue-100 rounded-lg p-3">
                  <div className="text-2xl">{lesson.emoji}</div>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-800 text-base mb-1">{lesson.title}</h3>
                  <p className="text-sm text-gray-500">Tap to start learning</p>
                </div>
                <div className="text-gray-400">→</div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Desktop: grid/list with header
  return (
    <div className="container mx-auto py-8 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 h-full relative overflow-y-auto">
      {/* Modern background elements */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,rgba(120,119,198,0.1),transparent_50%)]"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(255,119,198,0.1),transparent_50%)]"></div>
      
      <h1 className="text-3xl font-bold mb-6 text-center bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 bg-clip-text text-transparent relative z-10">Lessons</h1>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 relative z-10">
        {lessons.map((lesson, idx) => (
          <Link
            key={idx}
            to={lesson.link}
            className="rounded-2xl shadow-xl p-6 text-center text-xl font-bold bg-white/80 backdrop-blur-sm border border-white/30 hover:scale-105 transition-all duration-300 hover:shadow-2xl"
          >
            <div>
            <span className="text-4xl block mb-2">{lesson.emoji}</span>
              <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 bg-clip-text text-transparent">
            {lesson.title}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}